/**
 * The turn stream (slate-feature-transfer W8 §0.5, §B.3).
 *
 * `POST .../thoughts/{id}/messages/stream` answers `text/event-stream` with `event: <name>`
 * + `data: <json>` frames, camelCase, nulls omitted — a THIRD framing next to the two bare
 * `data:`+`[DONE]` lanes `refine-selection` and `sculpt` use (W8-22), which is why this reader
 * takes a framing rather than being written a third time.
 *
 * Three defects of the kms reader are fixed here by construction:
 *  - **W8-10** its `switch` had no `default`, so 11 of the server's events vanished —
 *    `skill_invocation`/`skill_result` among them. Here every known name is in the union and
 *    an unknown one arrives as `{event:"unknown", name}` with its payload intact.
 *  - **W8-9** it handled `moderator_synthesis`, which the server never emits; the real event
 *    is `merged_response` (camelCase `mergedFrom`). Moderator attribution was permanently
 *    lost. Both names are handled here, and `merged_response` is the one that carries it.
 *  - **W8-21** the server writes the SSE headers BEFORE the auth check, so "unauthorized"
 *    arrives as an `error` FRAME inside HTTP 200 and `if (!response.ok)` never fires. An
 *    error frame is a rejection here, and `Unauthorized` becomes a session error, not a
 *    model error.
 *
 * The event names were read out of `DocumentThoughtsController` on 2026-09-09 (28 distinct
 * `sendEvent(...)` names, including `moderator_synthesis_start`, which the wave spec's list
 * omits).
 */
import { ApiError, UnauthorizedError } from "~/api/errors";

export const CONVERSATION_STREAM_EVENTS = [
  "message_created",
  "processing_start",
  "context_info",
  "thinker_start",
  "thinker_skipped",
  "thinker_empty",
  "thinker_error",
  "ai_delta",
  "skill_invocation",
  "skill_result",
  "ai_response",
  "thinker_revision_start",
  "revision_delta",
  "thinker_revision_complete",
  "moderator_skipped",
  "moderator_start",
  "moderator_evaluation_start",
  "moderator_evaluation_complete",
  "moderator_evaluation_failed",
  "moderator_max_rounds",
  "moderator_reinstruction",
  "moderator_synthesis_start",
  "moderator_delta",
  /** Declared by kms, NEVER emitted by the server. Handled so nothing regresses if it is. */
  "moderator_synthesis",
  "merged_response",
  "moderator_error",
  "early_stop",
  "complete",
  "error",
] as const;

export type ConversationStreamEventName = (typeof CONVERSATION_STREAM_EVENTS)[number];

type Json = Record<string, unknown>;

/**
 * Payloads, as the server writes them. Only the fields the client reads are named; everything
 * else stays reachable through the same object, because a payload this file does not model is
 * still data the drawer may want to show.
 */
export interface ConversationStreamPayloads {
  message_created: Json & { id?: string; content?: string; author?: string };
  processing_start: Json & {
    messageId?: string;
    thinkers?: Array<{ thinkerId?: string; name?: string }>;
    thinkerCount?: number;
    forceAllThinkers?: boolean;
  };
  context_info: Json & {
    contextSizeBytes?: number;
    conversationMessages?: number;
    promptLength?: number;
  };
  thinker_start: Json & {
    thinkerId?: string;
    thinkerName?: string;
    model?: string;
    streamId?: string;
    inReplyTo?: string;
    contextSnapshotId?: string;
    contextItemIds?: string[];
    contextBytes?: number;
    startedAt?: string;
  };
  thinker_skipped: Json & { thinkerId?: string; reason?: string; streamId?: string };
  thinker_empty: Json & {
    thinkerId?: string;
    durationMs?: number;
    reason?: string;
    streamId?: string;
  };
  thinker_error: Json & { thinkerId?: string; error?: string; streamId?: string };
  ai_delta: Json & {
    streamId?: string;
    delta?: string;
    thinkerId?: string;
    model?: string;
    inReplyTo?: string;
  };
  skill_invocation: Json & {
    streamId?: string;
    thinkerId?: string;
    skillId?: string;
    query?: string;
  };
  skill_result: Json & {
    streamId?: string;
    thinkerId?: string;
    skillId?: string;
    result?: unknown;
  };
  ai_response: Json & {
    id?: string;
    content?: string;
    author?: string;
    authorType?: string;
    createdAt?: string;
    messageType?: string;
    streamId?: string;
    metadata?: Json;
    usage?: {
      inputTokens?: number;
      outputTokens?: number;
      totalTokens?: number;
      inputCost?: number;
      outputCost?: number;
      totalCost?: number;
      model?: string;
      durationMs?: number;
    };
  };
  thinker_revision_start: Json & { thinkerId?: string; streamId?: string; round?: number };
  revision_delta: Json & { streamId?: string; delta?: string; thinkerId?: string };
  thinker_revision_complete: Json & { thinkerId?: string; streamId?: string; content?: string };
  moderator_skipped: Json & { reason?: string };
  moderator_start: Json & {
    streamId?: string;
    phase?: string;
    responsesCount?: number;
    thinkerIds?: string[];
    maxRounds?: number;
    startedAt?: string;
    isModerator?: boolean;
  };
  moderator_evaluation_start: Json & { round?: number; maxRounds?: number };
  moderator_evaluation_complete: Json & {
    round?: number;
    overallAssessment?: string;
    isSatisfactory?: boolean;
    readyForSynthesis?: boolean;
    assessments?: unknown[];
  };
  moderator_evaluation_failed: Json & { round?: number; error?: string };
  moderator_max_rounds: Json & { rounds?: number };
  moderator_reinstruction: Json & {
    thinkerId?: string;
    round?: number;
    reason?: string;
    focusAreas?: string[];
  };
  moderator_synthesis_start: Json & { streamId?: string; responsesCount?: number };
  moderator_delta: Json & { streamId?: string; delta?: string };
  moderator_synthesis: Json;
  /** camelCase `mergedFrom` — NOT PascalCase `SynthesizedFrom` (W8-9). */
  merged_response: Json & {
    id?: string;
    content?: string;
    author?: string;
    authorType?: string;
    createdAt?: string;
    messageType?: string;
    streamId?: string;
    mergedFrom?: string[];
    responsesCount?: number;
    durationMs?: number;
    autoMerged?: boolean;
    mergeType?: string;
    evaluationRounds?: number;
    hadRevisions?: boolean;
    metadata?: Json;
  };
  moderator_error: Json & { error?: string; streamId?: string };
  early_stop: Json & {
    reason?: string;
    skippedThinkers?: string[];
    responsesCollected?: number;
    budgetSaved?: number;
  };
  complete: Json & {
    messageId?: string;
    aiResponseCount?: number;
    moderatorSynthesisIncluded?: boolean;
    stoppedEarly?: boolean;
    stopReason?: string;
    skippedThinkers?: string[];
  };
  error: Json & { error?: string };
}

export type ConversationStreamEvent =
  | {
      [K in ConversationStreamEventName]: { event: K; data: ConversationStreamPayloads[K] };
    }[ConversationStreamEventName]
  /** Never dropped: an event this build does not know still reaches the consumer. */
  | { event: "unknown"; name: string; data: Json };

export type ConversationStreamHandler = (event: ConversationStreamEvent) => void;

const KNOWN = new Set<string>(CONVERSATION_STREAM_EVENTS);

/** Streaming rows carry synthetic ids until the final message replaces them (§B.3). */
export function streamRowId(streamId: string): string {
  return `stream:${streamId}`;
}
export function moderatorRowId(streamId: string): string {
  return `moderator:${streamId}`;
}

function toEvent(name: string, raw: string): ConversationStreamEvent | null {
  let data: Json = {};
  if (raw.length > 0) {
    try {
      const parsed: unknown = JSON.parse(raw);
      data = parsed && typeof parsed === "object" ? (parsed as Json) : { value: parsed };
    } catch {
      // A torn or non-JSON frame is dropped rather than aborting the stream.
      return null;
    }
  }
  if (KNOWN.has(name)) {
    return { event: name, data } as ConversationStreamEvent;
  }
  return { event: "unknown", name, data };
}

/**
 * A carry-buffer line scanner. The chunk boundary is the whole reason this is a separate,
 * directly testable function: a `data:` line split across two network reads must produce ONE
 * event, and the only way to know it does is to feed it split.
 */
export function createEventStreamParser(onEvent: ConversationStreamHandler): {
  push(text: string): void;
  flush(): void;
} {
  let buffer = "";

  const emitFrame = (frame: string): void => {
    let name = "message";
    const payload: string[] = [];
    for (const line of frame.split("\n")) {
      const clean = line.endsWith("\r") ? line.slice(0, -1) : line;
      if (clean.startsWith(":") || clean.length === 0) {
        continue;
      }
      if (clean.startsWith("event:")) {
        name = clean.slice(6).trim();
      } else if (clean.startsWith("data:")) {
        payload.push(clean.slice(5).trimStart());
      }
    }
    if (payload.length === 0) {
      return;
    }
    const event = toEvent(name, payload.join("\n").trim());
    if (event) {
      onEvent(event);
    }
  };

  return {
    push(text: string) {
      buffer += text;
      const frames = buffer.split("\n\n");
      buffer = frames.pop() ?? "";
      for (const frame of frames) {
        emitFrame(frame);
      }
    },
    flush() {
      const rest = buffer.trim();
      buffer = "";
      if (rest.length > 0) {
        emitFrame(rest);
      }
    },
  };
}

/**
 * An `error` frame inside a 200 is a rejection, not a success (W8-21). `Unauthorized` is a
 * session problem — surfacing it as a model failure is what made kms's streaming look flaky
 * when it was really logged out.
 */
export function streamErrorFrom(message: string): ApiError {
  const text = message.trim() || "The assistant could not answer";
  return /unauthori[sz]ed/i.test(text)
    ? new UnauthorizedError("Your session has expired")
    : new ApiError(text, 500);
}

/**
 * Read a whole `text/event-stream` body. Resolves on `complete`/end-of-body, rejects on an
 * `error` frame (after handing it to the consumer, which still wants to render it) and on
 * abort — kms had no `AbortSignal` at all, so an abandoned stream hung until the tab closed.
 */
export async function readEventStream(
  stream: ReadableStream<Uint8Array>,
  onEvent: ConversationStreamHandler,
  signal?: AbortSignal,
): Promise<void> {
  const reader = stream.getReader();
  const decoder = new TextDecoder();
  // A holder rather than a `let`: the assignment happens inside the parser callback, and a
  // captured `let` would keep its initialiser's narrowing at every read below.
  const state: { failure: ApiError | null } = { failure: null };
  const parser = createEventStreamParser((event) => {
    onEvent(event);
    if (event.event === "error") {
      state.failure = streamErrorFrom(String(event.data.error ?? ""));
    }
  });

  try {
    for (;;) {
      if (signal?.aborted) {
        await reader.cancel().catch(() => undefined);
        throw new ApiError("Stopped", 499);
      }
      const { done, value } = await reader.read();
      if (done) {
        break;
      }
      parser.push(decoder.decode(value, { stream: true }));
      if (state.failure !== null) {
        await reader.cancel().catch(() => undefined);
        break;
      }
    }
    if (state.failure === null) {
      parser.flush();
    }
  } finally {
    reader.releaseLock();
  }

  if (state.failure !== null) {
    throw state.failure;
  }
}

/** The same reader over a fixture tape — how FakeApi proves the whole streaming path. */
export async function readEventText(
  chunks: string[],
  onEvent: ConversationStreamHandler,
  signal?: AbortSignal,
): Promise<void> {
  const encoder = new TextEncoder();
  const stream = new ReadableStream<Uint8Array>({
    start(controller) {
      for (const chunk of chunks) {
        controller.enqueue(encoder.encode(chunk));
      }
      controller.close();
    },
  });
  return readEventStream(stream, onEvent, signal);
}

/** Frames a scripted tape the way the server does — used by the fake and by the tests. */
export function encodeEvent(name: string, data: unknown): string {
  return `event: ${name}\ndata: ${JSON.stringify(data)}\n\n`;
}
